import { ensureJourneyDb, getById, insertRow, listTable, rowsFromIndex, writeAudit } from "../../db";
import { createMessage, createThread, createTransaction } from "../../db/schema";
import { resolveActor } from "./access";
import { badRequest, forbidden } from "./errors";
import { notifyUser } from "./helpers";

function isOfficeTx(transaction) {
  return transaction?.client_role === "office";
}

export function isOfficeParty(transaction, userId) {
  if (!transaction || !userId) return false;
  return [transaction.broker_id, transaction.agent_id, transaction.client_id].includes(userId);
}

export function listOfficePeers(session) {
  ensureJourneyDb();
  const actor = resolveActor(session);
  if (actor.journeyRole !== "broker" && actor.journeyRole !== "agent" && !actor.isAdmin) {
    throw forbidden("Only brokers and agents can send office inquiries");
  }
  const me = getById("users", actor.userId);
  const brokerageId = me?.brokerage_id || (actor.journeyRole === "broker" ? actor.userId : null);
  return listTable("users").filter((row) => {
    if (row.id === actor.userId || row.status === "inactive") return false;
    if (row.role_type !== "agent" && row.role_type !== "broker") return false;
    if (actor.isAdmin) return true;
    if (actor.journeyRole === "broker") return row.brokerage_id === actor.userId || row.id === actor.userId;
    return row.id === brokerageId || row.brokerage_id === brokerageId;
  });
}

function findOpenOfficeTx(fromId, toId) {
  return listTable("transactions").find((tx) => {
    if (!isOfficeTx(tx) || tx.status !== "open") return false;
    return isOfficeParty(tx, fromId) && isOfficeParty(tx, toId);
  });
}

function ensureOfficeTransaction(actor, recipient) {
  const existing = findOpenOfficeTx(actor.userId, recipient.id);
  if (existing) return existing;

  const sender = getById("users", actor.userId);
  const brokerageId =
    sender?.brokerage_id ||
    (actor.journeyRole === "broker" ? actor.userId : recipient.brokerage_id || recipient.id);
  const agentId = actor.journeyRole === "agent" ? actor.userId : recipient.role_type === "agent" ? recipient.id : actor.userId;
  const counterpartId = recipient.id === agentId ? actor.userId : recipient.id;

  return insertRow(
    "transactions",
    createTransaction({
      broker_id: brokerageId,
      agent_id: agentId,
      client_id: counterpartId,
      client_role: "office",
      property_address: `Office inquiry — ${recipient.name || recipient.email}`,
      status: "open",
    })
  );
}

function ensureThread(transaction, actor) {
  const threads = rowsFromIndex("message_threads", "threadsByTransactionId", transaction.id);
  const active = threads.find((item) => item.status !== "archived") || threads[0];
  if (active) return active;
  return insertRow(
    "message_threads",
    createThread({
      transaction_id: transaction.id,
      created_by_user_id: actor.userId,
      status: "active",
    })
  );
}

/** POST /api/inquiries/send — broker→agent or agent→agent, stored in Office Messaging. */
export function postInquirySend(body = {}, session) {
  ensureJourneyDb();
  const actor = resolveActor(session);
  if (actor.journeyRole !== "broker" && actor.journeyRole !== "agent" && !actor.isAdmin) {
    throw forbidden("Only brokers and agents can send office inquiries");
  }
  const toUserId = body.to_user_id;
  const text = String(body.message_text || "").trim();
  if (!toUserId) throw badRequest("Choose someone to notify");
  if (!text) throw badRequest("Write a short inquiry first");
  if (toUserId === actor.userId) throw badRequest("Choose a colleague, not yourself");

  const recipient = getById("users", toUserId);
  if (!recipient || (recipient.role_type !== "agent" && recipient.role_type !== "broker")) {
    throw badRequest("Inquiries go to agents or brokers in your office");
  }

  const transaction = ensureOfficeTransaction(actor, recipient);
  const thread = ensureThread(transaction, actor);
  const message = insertRow(
    "messages",
    createMessage({
      thread_id: thread.id,
      sender_id: actor.userId,
      receiver_id: recipient.id,
      message_text: text,
      tag: body.tag === "betaFeedback" ? "betaFeedback" : null,
    })
  );

  writeAudit({
    user_id: actor.userId,
    action_type: "inquiry.create",
    transaction_id: transaction.id,
    metadata: { thread_id: thread.id, message_id: message.id, to_user_id: recipient.id },
  });

  notifyUser({
    user_id: recipient.id,
    type: "inquiry",
    payload: {
      thread_id: thread.id,
      transaction_id: transaction.id,
      message_id: message.id,
      from_name: actor.name,
    },
  });

  return {
    transaction_id: transaction.id,
    thread_id: thread.id,
    message,
  };
}
